import { supabase } from '../../supabaseClient';
import { queueSyncTask } from '../../utils/offlineSync';
import { SERVICE_SEARCH_TIMEOUT_SEC, SERVICE_SEARCH_DIAGNOSTIC_WINDOW_SEC, TRACKING_STATUSES, SERVICE_STATUSES, PAYMENT_METHODS, ROLES } from '../../constants';
import { calcularDistancia } from '../../components/DELIVERY/utils/tarifaCalculator';
import { buildVisibilityDiagnostics, getServiceDistanceKm } from '../driverServiceVisibility';
import { emailService } from '../emailService';
import { deductServiceCommission } from './diary';

// ========================
// SERVICIOS DE DELIVERY
// ========================

/**
 * Obtiene la configuración de tarifas actual
 */
export async function getTariffConfig() {
  const { data, error } = await supabase
    .from('tariffs_config')
    .select('id, base_price, price_per_km, price_per_minute, gas_per_km')
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data || { base_price: 2.0, price_per_km: 0.60, price_per_minute: 0.15, gas_per_km: 0.018 };
}

/**
 * Actualiza la configuración de tarifas (solo admin)
 */
export async function updateTariffConfig(config) {
  const { error } = await supabase
    .from('tariffs_config')
    .upsert({ ...config, updated_at: new Date().toISOString() });
  if (error) throw error;
}

// ========================
// SERVICIOS (VIAJES/ENCOMIENDAS)
// ========================

/**
 * Crea un nuevo servicio de ride/delivery
 */
export async function createService(serviceData) {
  const servicePayload = {
    service_id: serviceData.service_id || `SRV-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    ...serviceData,
  };
  const { data, error } = await supabase
    .from('services')
    .insert(servicePayload)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Obtener servicios del usuario (como cliente)
 */
export async function getUserServices(userId) {
  const { data, error } = await supabase
    .from('services')
    .select('service_id, user_uid, driver_uid, status, type, subcategory_id, pickup, dropoff, price, distance, commission, payment_method, created_at, tracking_token, offered_price, base_price, price_per_km, price_per_min, min_price, passenger_count, reference_points')
    .eq('user_uid', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Obtener servicios asignados al conductor
 */
export async function getDriverServices(driverUid) {
  const { data, error } = await supabase
    .from('services')
    .select('service_id, user_uid, driver_uid, status, type, subcategory_id, pickup, dropoff, price, distance, commission, created_at, offered_price, base_price, price_per_km, price_per_min, min_price, passenger_count, reference_points')
    .eq('driver_uid', driverUid)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Actualizar estado de un servicio con soporte Offline
 */
export async function updateServiceStatus(serviceId, status, driverUid = null) {
  if (!serviceId) throw new Error('serviceId es requerido para actualizar el estado');
  const update = { status };
  if (driverUid) update.driver_uid = driverUid;

  try {
    let query = supabase.from('services').update(update);
    if (typeof serviceId === 'string' && serviceId.includes('-') && serviceId.length === 36 && !serviceId.startsWith('SRV-')) {
      query = query.eq('id', serviceId);
    } else {
      query = query.eq('service_id', serviceId);
    }
    const { error } = await query;
    
    if (error) throw error;

    if (status === SERVICE_STATUSES.COMPLETED || status === 'completed') {
      triggerCompletionReceiptEmail(serviceId).catch(err => console.warn('Non-blocking receipt email error:', err));
    }
  } catch (err) {
    if (!navigator.onLine || err.message?.includes('Fetch')) {
      console.warn('[Offline] Sin conexión. Encolando cambio de estado.');
      await queueSyncTask('UPDATE_SERVICE_STATUS', { serviceId, status, driverUid });
      return;
    }
    throw err;
  }
}

// ========================
// CONDUCTORES
// ========================

/**
 * Registrar/actualizar perfil de conductor
 */
export async function upsertDriverProfile(driverData) {
  const { name, phone, brand, model, year, color, vehicle_type, ...rest } = driverData;
  
  const driverTableData = {
    ...rest,
    ...(vehicle_type !== undefined && { vehicle_type }),
    ...(brand !== undefined && { vehicle_brand: brand }),
    ...(model !== undefined && { vehicle_model: model }),
    ...(year !== undefined && { vehicle_year: year }),
    ...(color !== undefined && { vehicle_color: color }),
    ...(name !== undefined && { name }),
    ...(phone !== undefined && { phone })
  };
  
  const { error: driverError } = await supabase
    .from('drivers')
    .upsert(driverTableData, { onConflict: 'driver_uid' });
  if (driverError) throw driverError;

  if (name || phone) {
    const profileUpdate = {};
    if (name) profileUpdate.name = name;
    if (phone) profileUpdate.phone = phone;
    
    const { error: profileError } = await supabase
      .from('profiles')
      .update(profileUpdate)
      .eq('id', driverData.driver_uid);
      
    if (profileError) console.error("Error updating profile name/phone:", profileError);
  }

  // Assign driver role ONLY when status is ACTIVE (approved by admin)
  if (driverData.status === 'active' || driverData.status === 'ACTIVE') {
    try {
      const { data: roleData } = await supabase
        .from('roles')
        .select('id')
        .eq('name', ROLES.DRIVER)
        .single();
      
      if (roleData) {
        await supabase
          .from('user_roles')
          .upsert({
            user_id: driverData.driver_uid,
            role_id: roleData.id,
            assigned_by: driverData.driver_uid,
            is_active: true
          }, { onConflict: 'user_id,role_id' });
      }
    } catch (error) {
      console.error("Error assigning driver role:", error);
    }
  }

  // Create a default vehicle entry if this is a new registration with vehicle_type
  if (vehicle_type && driverData.driver_uid) {
    const { data: existingVehicles } = await supabase
      .from('vehicles')
      .select('id')
      .eq('driver_uid', driverData.driver_uid)
      .limit(1);
    
    if (!existingVehicles || existingVehicles.length === 0) {
      const { error: vehicleError } = await supabase
        .from('vehicles')
        .insert({
          driver_uid: driverData.driver_uid,
          vehicle_type,
          plate: driverData.plate || 'PENDIENTE',
          is_default: true,
        });
      if (vehicleError) console.error("Error creating default vehicle:", vehicleError);
    }
  }
}

/**
 * Obtener perfil de conductor
 */
export async function getDriverProfile(driverUid) {
  // Obtenemos el registro en drivers, que puede estar pendiente o activo
  const { data: driverData, error: driverError } = await supabase
    .from('drivers')
    .select('driver_uid, rating, total_trips, vehicle_type, plate, status, is_approved, vehicle_brand, vehicle_model, vehicle_year, vehicle_color')
    .eq('driver_uid', driverUid)
    .maybeSingle();
  if (driverError) throw driverError;

  if (driverData) {
    // Buscamos los datos del usuario en profiles
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('name, phone')
      .eq('id', driverUid)
      .maybeSingle();

    if (profileError) throw profileError;

    return {
      ...driverData,
      brand: driverData.vehicle_brand,
      model: driverData.vehicle_model,
      year: driverData.vehicle_year,
      color: driverData.vehicle_color,
      driver_status: driverData.status,
      name: profileData?.name,
      phone: profileData?.phone || driverData.phone
    };
  }

  return null;
}

/**
 * Actualizar ubicación del conductor con soporte Offline (Solo sincronización)
 */
export async function updateDriverLocation(driverUid, lat, lng, status = TRACKING_STATUSES.ONLINE) {
  const geohash = `${lat.toFixed(3)}${lng.toFixed(3)}`.replace(/\./g, '');
  const data = {
    driver_uid: driverUid,
    lat,
    lng,
    geohash,
    status,
    last_updated: new Date().toISOString(),
  };

  try {
    const { error } = await supabase
      .from('drivers_tracking')
      .upsert(data, { onConflict: 'driver_uid' });
    
    if (error) throw error;
  } catch (err) {
    // Si falla la ubicación, no la encolamos (es datos volátiles), 
    // pero guardamos el log por si es crítico
    if (!navigator.onLine) return;
    throw err;
  }
}

/**
 * Obtener conductores cercanos (online y ocupados) con distancia calculada
 */
export async function getNearbyDrivers(lat, lng, radiusKm = 5) {
  if (isNaN(lat) || isNaN(lng) || lat == null || lng == null) {
    return [];
  }

  const delta = radiusKm / 111;
  const { data, error } = await supabase
    .from('drivers_tracking')
    .select(`
      driver_uid, 
      lat, 
      lng, 
      status, 
      geohash, 
      last_trip_timestamp
    `)
    .in('status', [TRACKING_STATUSES.ONLINE, TRACKING_STATUSES.ON_TRIP])
    .gte('lat', lat - delta)
    .lte('lat', lat + delta)
    .gte('lng', lng - delta)
    .lte('lng', lng + delta)
    .order('last_trip_timestamp', { ascending: true, nullsFirst: true });
    
  if (error) {
    console.warn("Fallo getNearbyDrivers:", error);
    return [];
  }

  const trackingData = data || [];
  if (trackingData.length === 0) return [];

  const uids = trackingData.map(d => d.driver_uid).filter(Boolean);
  
  if (uids.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select(`
        id, 
        name, 
        avatar_url,
        drivers ( rating, total_trips, is_approved, vehicle_type, plate )
      `)
      .in('id', uids);
      
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    
    return trackingData.map(d => {
      const p = profileMap[d.driver_uid] || {};
      // Handle drivers relation which could be an array or an object
      const dInfo = Array.isArray(p.drivers) ? p.drivers[0] : (p.drivers || {});
      return {
        ...d,
        name: p.name || 'Conductor',
        avatar_url: p.avatar_url || null,
        vehicle_type: dInfo?.vehicle_type || d.vehicle_type || null,
        plate: dInfo?.plate || null,
        is_approved: !!dInfo?.is_approved,
        rating: dInfo?.rating,
        total_trips: dInfo?.total_trips,
        distance_km: calcularDistancia(lat, lng, d.lat, d.lng),
      };
    });
  }

  return trackingData;
}


/**
 * Obtiene la ubicación actual de un conductor desde drivers_tracking
 */
export async function getDriverLocation(driverUid) {
  const { data, error } = await supabase
    .from('drivers_tracking')
    .select('lat, lng, status')
    .eq('driver_uid', driverUid)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Obtiene el tipo de vehículo del conductor (default vehicle)
 */
export async function getDriverActiveVehicleTypes(driverUid) {
  if (!driverUid) return [];
  const types = new Set();
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .select('vehicle_type, is_active')
      .eq('driver_uid', driverUid);
    if (!error && Array.isArray(data)) {
      data.forEach((v) => {
        if (v.vehicle_type && v.is_active !== false) {
          types.add(v.vehicle_type);
        }
      });
    } else if (!error && data && typeof data === 'object' && data.vehicle_type) {
      types.add(data.vehicle_type);
    }
  } catch (err) {
    console.warn('Error fetching active vehicle types:', err);
  }
  if (types.size > 0) {
    return Array.from(types);
  }
  const defaultType = await getDriverVehicleType(driverUid).catch(() => null);
  return defaultType ? [defaultType] : [];
}

export async function getDriverVehicleType(driverUid) {
  if (!driverUid) return null;
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .select('vehicle_type')
      .eq('driver_uid', driverUid)
      .eq('is_default', true)
      .maybeSingle();
    if (!error && data?.vehicle_type) {
      return data.vehicle_type;
    }
  } catch (err) {
    console.warn('Error fetching default vehicle from vehicles:', err);
  }

  // Fallback a public.drivers cuando el vehículo aún no fue sincronizado a public.vehicles
  try {
    const { data: driverData } = await supabase
      .from('drivers')
      .select('vehicle_type')
      .eq('driver_uid', driverUid)
      .maybeSingle();
    return driverData?.vehicle_type || null;
  } catch (err) {
    console.warn('Error fetching fallback vehicle_type from drivers:', err);
    return null;
  }
}

/**
 * Obtiene servicios searching cercanos al conductor, filtrados por proximidad y tipo de vehículo.
 * @param {string} driverUid - UID del conductor
/**
 * Obtener preferencias de categorías de servicio del conductor.
 * Devuelve todas las categorías activas con el flag is_enabled (true por defecto).
 * @param {string} driverUid
 * @returns {Promise<Array>}
 */
export async function getDriverServicePreferences(driverUid) {
  if (!driverUid) return [];

  const { data: categories, error: catError } = await supabase
    .from('service_categories')
    .select('id, name, slug, description, icon, color, sort_order')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (catError) throw catError;
  if (!categories || categories.length === 0) return [];

  const { data: prefs, error: prefError } = await supabase
    .from('driver_service_preferences')
    .select('category_id, is_enabled')
    .eq('driver_uid', driverUid);

  if (prefError) {
    console.warn('Error cargando preferencias de conductor:', prefError);
    return categories.map(c => ({ ...c, is_enabled: true }));
  }

  const prefsMap = new Map();
  (prefs || []).forEach(p => prefsMap.set(p.category_id, p.is_enabled));

  return categories.map(c => ({
    ...c,
    is_enabled: prefsMap.has(c.id) ? prefsMap.get(c.id) : true,
  }));
}

/**
 * Guardar o actualizar la preferencia de una categoría de servicio para el conductor.
 * @param {string} driverUid
 * @param {string} categoryId
 * @param {boolean} isEnabled
 * @returns {Promise<Object>}
 */
export async function setDriverServicePreference(driverUid, categoryId, isEnabled) {
  if (!driverUid || !categoryId) throw new Error('driverUid and categoryId are required');

  const { data, error } = await supabase
    .from('driver_service_preferences')
    .upsert(
      {
        driver_uid: driverUid,
        category_id: categoryId,
        is_enabled: isEnabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'driver_uid,category_id' }
    )
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Obtener servicios disponibles y compatibles para el conductor
 * (status: searching, distancia <= radiusKm, mismo tipo de vehículo,
 * y dentro de las categorías de servicio habilitadas por el conductor).
 *
 * @param {string} driverUid - UID del conductor
 * @param {number} radiusKm - Radio de búsqueda en km (default: 10)
 * @returns {Array} Servicios compatibles ordenados por distancia
 */
export async function getCompatibleServicesWithDiagnostics(driverUid, radiusKm = 10, fallbackLocation = null) {
  let location = await getDriverLocation(driverUid).catch(() => null);
  const hasValidStoredCoords =
    location?.lat != null &&
    location?.lng != null &&
    !(Number(location.lat) === 0 && Number(location.lng) === 0);

  if (!hasValidStoredCoords && fallbackLocation) {
    const fbLat = fallbackLocation.latitude ?? fallbackLocation.lat;
    const fbLng = fallbackLocation.longitude ?? fallbackLocation.lng;
    if (fbLat != null && fbLng != null && !(Number(fbLat) === 0 && Number(fbLng) === 0)) {
      location = { lat: Number(fbLat), lng: Number(fbLng) };
    }
  }

  const activeVehicleTypes = await getDriverActiveVehicleTypes(driverUid);

  // Categorías deshabilitadas explícitamente por el conductor
  let disabledSlugs = new Set();
  const disabledCategories = {};
  try {
    const { data: disabledPrefs } = await supabase
      .from('driver_service_preferences')
      .select('category_id, service_categories(slug, name)')
      .eq('driver_uid', driverUid)
      .eq('is_enabled', false);

    if (disabledPrefs && disabledPrefs.length > 0) {
      disabledPrefs.forEach(p => {
        const slug = p.service_categories?.slug;
        if (slug) {
          disabledSlugs.add(slug);
          disabledCategories[slug] = p.service_categories?.name || slug;
        }
      });
    }
  } catch (err) {
    console.warn('Error recuperando preferencias en getCompatibleServicesForDriver:', err);
  }

  // Traemos también las ya expiradas (dentro de una ventana) para poder explicar el bloqueo
  const diagnosticWindowStart = new Date(
    Date.now() - SERVICE_SEARCH_DIAGNOSTIC_WINDOW_SEC * 1000
  ).toISOString();

  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('status', SERVICE_STATUSES.SEARCHING)
    .gte('created_at', diagnosticWindowStart)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;

  const services = data || [];

  const diagnostics = buildVisibilityDiagnostics(services, {
    location,
    radiusKm,
    activeVehicleTypes,
    disabledSlugs: Array.from(disabledSlugs),
    now: Date.now(),
    timeoutSec: SERVICE_SEARCH_TIMEOUT_SEC
  });

  const visibleServices = diagnostics.visibleServices
    .map(s => ({ ...s, _distance: getServiceDistanceKm(s, location) }))
    .sort((a, b) => a._distance - b._distance);

  return {
    services: visibleServices,
    diagnostics: {
      ...diagnostics,
      visibleServices: undefined,
      disabledCategories
    }
  };
}

/**
 * Servicios compatibles ordenados por distancia (contrato legacy: devuelve un array).
 * @returns {Array}
 */
export async function getCompatibleServicesForDriver(driverUid, radiusKm = 10, fallbackLocation = null) {
  const { services } = await getCompatibleServicesWithDiagnostics(driverUid, radiusKm, fallbackLocation);
  return services;
}

// ========================
// TRACKING PÚBLICO
// ========================

/**
 * Obtener info de tracking por token público
 */
export async function getTrackingByToken(token) {
  const { data, error } = await supabase
    .rpc('get_tracking_by_token', { token });
  if (error) throw error;
  return data;
}

/**
 * Obtener el tracking_token de un servicio
 */
export async function getServiceTrackingToken(serviceId) {
  const { data, error } = await supabase
    .from('services')
    .select('tracking_token')
    .eq('service_id', serviceId)
    .maybeSingle();
  if (error) throw error;
  return data?.tracking_token || null;
}

export async function registerPayment(paymentData) {
  const { data, error } = await supabase
    .from('payments')
    .insert(paymentData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Obtener pagos de un servicio
 */
export async function getServicePayments(serviceId) {
  const { data, error } = await supabase
    .from('payments')
    .select('id, service_id, amount, currency, payment_method, reference, status, created_at')
    .eq('service_id', serviceId);
  if (error) throw error;
  return data || [];
}

/**
 * Confirmar servicio mediante token QR
 */
export async function confirmServiceByToken(token) {
  const { data, error } = await supabase
    .rpc('confirm_service_by_token', { token });
  if (error) throw error;
  // Normalizar la respuesta: el RPC puede retornar true, false, o { success, error }
  if (typeof data === 'boolean') {
    return { success: data };
  }
  if (data && typeof data === 'object') {
    return data; // Ya tiene la forma { success, error }
  }
  return { success: !!data };
}

/**
 * Obtener el confirmation_token de un servicio
 */
export async function getServiceConfirmationToken(serviceId) {
  const { data, error } = await supabase
    .from('services')
    .select('confirmation_token')
    .eq('service_id', serviceId)
    .maybeSingle();
  if (error) throw error;
  return data?.confirmation_token || null;
}

// ========================
// P2P PAYMENTS
// ========================

function normalizeRpcResult(data) {
  if (typeof data === 'boolean') return { success: data };
  if (data && typeof data === 'object') return data;
  return { success: !!data };
}

export async function requestPayment(serviceId, amount, currency = 'VES', paymentMethod = PAYMENT_METHODS.PAGO_MOVIL) {
  const { data, error } = await supabase
    .rpc('request_payment', { p_service_ref: serviceId, p_amount: amount, p_currency: currency, p_payment_method: paymentMethod });
  if (error) throw error;
  return normalizeRpcResult(data);
}

export async function markPaymentAsPaid(paymentId, reference, paymentMethod = null) {
  const { data, error } = await supabase
    .rpc('mark_payment_as_paid', { p_payment_id: paymentId, p_reference: reference, p_payment_method: paymentMethod });
  if (error) throw error;
  return normalizeRpcResult(data);
}

export async function confirmPayment(paymentId) {
  const { data, error } = await supabase
    .rpc('confirm_payment', { p_payment_id: paymentId });
  if (error) throw error;
  return normalizeRpcResult(data);
}

export async function getServicePayment(serviceId) {
  const { data, error } = await supabase
    .rpc('get_service_payment', { p_service_ref: serviceId });
  if (error) throw error;
  return data;
}

// Helper functions for automated notification emails
export async function triggerDriverAssignedEmail(serviceId, driverUid) {
  try {
    const { data: service } = await supabase
      .from('services')
      .select('user_uid, pickup, dropoff, price')
      .eq('service_id', serviceId)
      .maybeSingle();
    
    if (!service?.user_uid) return;

    const { data: passenger } = await supabase
      .from('profiles')
      .select('email, name')
      .eq('id', service.user_uid)
      .maybeSingle();

    const { data: driver } = await supabase
      .from('profiles')
      .select('name, phone')
      .eq('id', driverUid)
      .maybeSingle();

    if (passenger?.email) {
      await emailService.sendNotificationEmail({
        to: passenger.email,
        subject: '¡Conductor asignado a tu viaje en Bryemma!',
        html: `
          <div style="font-family: Arial, sans-serif; padding: 20px; color: #0f172a;">
            <h2 style="color: #0EABB0;">🚗 Conductor Asignado</h2>
            <p>Hola ${passenger.name || 'Cliente'}, un conductor ha aceptado tu servicio.</p>
            <p><strong>Conductor:</strong> ${driver?.name || 'Conductor Bryemma'}</p>
            <p><strong>Teléfono:</strong> ${driver?.phone || 'N/A'}</p>
            <p><strong>Origen:</strong> ${service.pickup?.address || 'Ubicación seleccionada'}</p>
            <p><strong>Destino:</strong> ${service.dropoff?.address || 'Destino seleccionado'}</p>
          </div>
        `
      });
    }
  } catch (err) {
    console.warn('Could not send driver assigned email:', err);
  }
}

export async function triggerCompletionReceiptEmail(serviceId) {
  try {
    const { data: service } = await supabase
      .from('services')
      .select('user_uid, pickup, dropoff, price, payment_method, driver_uid, commission, offered_price')
      .eq('service_id', serviceId)
      .maybeSingle();

    if (!service?.user_uid && !service?.driver_uid) return;

    // Deducción de comisión del 15% de la billetera del conductor
    if (service.driver_uid) {
      try {
        // RPC deduct_service_commission (v9h): monto derivado en BD, no
        // parámetro libre. El import '../../DB/supabaseServices' era una ruta
        // inexistente (la deducción jamás ejecutaba — bug C3 raíz).
        await deductServiceCommission(serviceId, service.driver_uid);
      } catch (commErr) {
        console.warn('Error in wallet commission deduction:', commErr);
      }
    }

    if (service.user_uid) {
      const { data: passenger } = await supabase
        .from('profiles')
        .select('email, name')
        .eq('id', service.user_uid)
        .maybeSingle();

      if (passenger?.email) {
        await emailService.sendNotificationEmail({
          to: passenger.email,
          subject: 'Tu recibo de viaje en Bryemma 🧾',
          html: `
            <div style="font-family: Arial, sans-serif; padding: 20px; color: #0f172a;">
              <h2 style="color: #0EABB0;">🧾 Recibo Digital de Servicio</h2>
              <p>Gracias por viajar con Bryemma, ${passenger.name || 'Cliente'}.</p>
              <p><strong>Monto Total:</strong> $${service.price}</p>
              <p><strong>Método de Pago:</strong> ${service.payment_method || 'Efectivo/Pago Móvil'}</p>
              <p><strong>Origen:</strong> ${service.pickup?.address || 'Ubicación seleccionada'}</p>
              <p><strong>Destino:</strong> ${service.dropoff?.address || 'Destino seleccionado'}</p>
            </div>
          `
        });
      }
    }
  } catch (err) {
    console.warn('Could not process completion receipt/commission:', err);
  }
}

// ========================
// NEGOCIATION (OFFER SYSTEM)
// ========================

export async function acceptServiceOffer(serviceId, driverUid) {
  const { data, error } = await supabase
    .rpc('accept_service_offer', { p_service_id: serviceId, p_driver_uid: driverUid });
  if (error) throw error;
  if (data && data.success !== false) {
    triggerDriverAssignedEmail(serviceId, driverUid).catch(err => console.warn('Non-blocking driver assigned email error:', err));
  }
  return data;
}

/**
 * Accept a searching service (non-offered path) via atomic RPC.
 * Uses FOR UPDATE lock to prevent race conditions.
 */
export async function acceptService(serviceId, driverUid) {
  const { data, error } = await supabase
    .rpc('accept_service', { p_service_id: serviceId, p_driver_uid: driverUid });
  if (error) throw error;
  triggerDriverAssignedEmail(serviceId, driverUid).catch(err => console.warn('Non-blocking driver assigned email error:', err));
  return data;
}

/**
 * Marca servicios searching antiguos como no_drivers_available.
 * Llamar periódicamente o al cargar la app.
 */
export async function expireStaleSearchingServices(timeoutSec = 180) {
  try {
    const { data, error } = await supabase.rpc('expire_stale_searching_services', {
      timeout_seconds: timeoutSec,
    });
    if (error) throw error;
    return data || 0;
  } catch (err) {
    console.error('Error expiring stale services:', err);
    return 0;
  }
}

// ========================
// VEHICLES (Multi-Vehicle)
// ========================

export const getDriverVehicles = async (driverUid) => {
  if (!driverUid) return [];
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('driver_uid', driverUid)
      .order('created_at', { ascending: false });
    if (!error && Array.isArray(data) && data.length > 0) {
      return data;
    }
  } catch (err) {
    console.warn('Error fetching driver vehicles:', err);
  }

  // Fallback de hidratación desde public.drivers cuando el conductor aún no tiene filas en public.vehicles
  try {
    const { data: driverData } = await supabase
      .from('drivers')
      .select('driver_uid, vehicle_type, plate, vehicle_brand, vehicle_model, vehicle_year, vehicle_color')
      .eq('driver_uid', driverUid)
      .maybeSingle();

    if (driverData?.vehicle_type) {
      return [{
        id: null,
        driver_uid: driverUid,
        vehicle_type: driverData.vehicle_type,
        plate: driverData.plate || 'SIN-PLACA',
        brand: driverData.vehicle_brand || null,
        model: driverData.vehicle_model || null,
        year: driverData.vehicle_year || null,
        color: driverData.vehicle_color || null,
        is_active: true,
        is_default: true,
        _from_driver_record: true,
      }];
    }
  } catch (err) {
    console.warn('Error fetching fallback vehicle from drivers:', err);
  }

  return [];
};

export const addVehicle = async (vehicle) => {
  if (!vehicle.driver_uid || !vehicle.plate) {
    throw new Error('El conductor y la placa del vehículo son requeridos');
  }

  const cleanPlate = vehicle.plate.toUpperCase().trim();
  const yearNum = vehicle.year && !isNaN(parseInt(vehicle.year)) ? parseInt(vehicle.year) : null;

  const payload = {
    driver_uid: vehicle.driver_uid,
    vehicle_type: vehicle.vehicle_type || 'moto',
    plate: cleanPlate,
    brand: vehicle.brand || null,
    model: vehicle.model || null,
    year: yearNum,
    color: vehicle.color || null,
    is_default: vehicle.is_default !== false,
    is_verified: true,
  };

  // Si se establece como default, desmarcar otros vehículos primero
  if (payload.is_default) {
    try {
      await supabase.from('vehicles').update({ is_default: false }).eq('driver_uid', vehicle.driver_uid);
    } catch (e) {
      console.warn('Error resetting previous defaults:', e);
    }
  }

  let insertedData = null;
  const { data, error } = await supabase.from('vehicles').insert(payload).select().single();
  if (!error && data) {
    insertedData = data;
  } else {
    // Fallback si falla por columnas opcionales
    const minPayload = {
      driver_uid: vehicle.driver_uid,
      vehicle_type: vehicle.vehicle_type || 'moto',
      plate: cleanPlate,
      brand: vehicle.brand || null,
      model: vehicle.model || null,
      is_default: payload.is_default
    };
    const { data: fbData, error: fbError } = await supabase.from('vehicles').insert(minPayload).select().single();
    if (fbError) throw fbError;
    insertedData = fbData;
  }

  // Sincronizar conductor con los nombres correctos de columnas en tabla drivers
  if (payload.is_default) {
    try {
      await supabase.from('drivers').upsert({
        driver_uid: vehicle.driver_uid,
        vehicle_type: payload.vehicle_type,
        plate: cleanPlate,
        vehicle_brand: payload.brand,
        vehicle_model: payload.model,
        vehicle_year: payload.year,
        vehicle_color: payload.color,
        updated_at: new Date().toISOString()
      }, { onConflict: 'driver_uid' });
    } catch (e) {
      console.warn('Error syncing driver vehicle:', e);
    }
  }

  return insertedData;
};

export const updateVehicle = async (id, updates) => {
  const cleanPlate = updates.plate ? updates.plate.toUpperCase().trim() : undefined;
  const yearNum = updates.year !== undefined ? (updates.year && !isNaN(parseInt(updates.year)) ? parseInt(updates.year) : null) : undefined;
  
  const payload = { ...updates };
  if (cleanPlate) payload.plate = cleanPlate;
  if (yearNum !== undefined) payload.year = yearNum;

  const { data, error } = await supabase.from('vehicles').update(payload).eq('id', id).select().single();
  if (error) throw error;

  if (updates.is_default && updates.driver_uid) {
    try {
      await supabase.from('vehicles').update({ is_default: false }).eq('driver_uid', updates.driver_uid).neq('id', id);
    } catch (e) {
      console.warn('Error resetting other defaults:', e);
    }
    try {
      await supabase.from('drivers').update({
        vehicle_type: updates.vehicle_type || data?.vehicle_type,
        plate: cleanPlate || data?.plate,
        vehicle_brand: updates.brand !== undefined ? updates.brand : data?.brand,
        vehicle_model: updates.model !== undefined ? updates.model : data?.model,
        vehicle_year: yearNum !== undefined ? yearNum : data?.year,
        vehicle_color: updates.color !== undefined ? updates.color : data?.color,
        updated_at: new Date().toISOString()
      }).eq('driver_uid', updates.driver_uid);
    } catch (e) {
      console.warn('Error syncing driver on update:', e);
    }
  }

  return data;
};

export const deleteVehicle = async (id) => {
  // 1. Obtener datos del vehículo antes de eliminar
  const { data: veh } = await supabase.from('vehicles').select('driver_uid, is_default').eq('id', id).maybeSingle();
  const { error } = await supabase.from('vehicles').delete().eq('id', id);
  if (error) throw error;

  if (veh?.driver_uid) {
    try {
      // Buscar si le queda algún otro vehículo
      const { data: remaining } = await supabase
        .from('vehicles')
        .select('*')
        .eq('driver_uid', veh.driver_uid)
        .order('created_at', { ascending: false });

      if (remaining && remaining.length > 0) {
        // Si el eliminado era el default, marcar el primero restante como default
        if (veh.is_default || !remaining.some(r => r.is_default)) {
          await setDefaultVehicle(veh.driver_uid, remaining[0].id);
        }
      } else {
        // Si ya no le queda ningún vehículo, limpiar las columnas de vehículo en drivers
        await supabase.from('drivers').update({
          vehicle_type: null,
          plate: null,
          vehicle_brand: null,
          vehicle_model: null,
          vehicle_year: null,
          vehicle_color: null,
          updated_at: new Date().toISOString()
        }).eq('driver_uid', veh.driver_uid);
      }
    } catch (e) {
      console.warn('Error in cascade delete vehicle cleanup:', e);
    }
  }
};

export const setDefaultVehicle = async (driverUid, vehicleId) => {
  try {
    await supabase.from('vehicles').update({ is_default: false }).eq('driver_uid', driverUid);
  } catch (e) {
    console.warn('Error resetting defaults:', e);
  }

  const { data: updatedVeh, error } = await supabase.from('vehicles').update({ is_default: true }).eq('id', vehicleId).select().single();
  if (error) throw error;

  if (updatedVeh) {
    try {
      await supabase.from('drivers').update({
        vehicle_type: updatedVeh.vehicle_type,
        plate: updatedVeh.plate,
        vehicle_brand: updatedVeh.brand,
        vehicle_model: updatedVeh.model,
        vehicle_year: updatedVeh.year,
        vehicle_color: updatedVeh.color,
        updated_at: new Date().toISOString()
      }).eq('driver_uid', driverUid);
    } catch (e) {
      console.warn('Error updating driver active vehicle:', e);
    }
  }

  return updatedVeh;
};

export const assignVehicleToService = async (serviceId, vehicleId) => {
  if (!serviceId || !vehicleId) return;
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(serviceId));
  const query = supabase.from('services').update({ vehicle_id: vehicleId });
  const { error } = isUuid
    ? await query.eq('id', serviceId)
    : await query.eq('service_id', serviceId);
  if (error) throw error;
};

// ========================
// GPS ANTI-SPOOF
// ========================

export async function validateAndUpdateTracking(driverUid, lat, lng, accuracy, speed) {
  const { data, error } = await supabase
    .rpc('validate_and_update_tracking', {
      p_uid: driverUid,
      p_lat: lat,
      p_lng: lng,
      p_accuracy: accuracy,
      p_speed: speed,
    });
  if (error) throw error;
  return data;
}

export async function getSuspiciousDrivers() {
  const { data, error } = await supabase
    .rpc('get_suspicious_drivers');
  if (error) throw error;
  return data || [];
}

// ========================
// VIP / RECURRING TRANSPORT
// ========================

export async function getDueScheduledServices() {
  const { data, error } = await supabase.rpc('get_due_scheduled_services');
  if (error) throw error;
  return data || [];
}

export async function autoAssignScheduledService(scheduledId) {
  const { data, error } = await supabase.rpc('auto_assign_scheduled_service', {
    sched_id: scheduledId,
  });
  if (error) throw error;
  return data;
}

export async function getPassengerScheduledServices(passengerUid) {
  const { data, error } = await supabase.rpc('get_passenger_scheduled_services', {
    p_uid: passengerUid,
  });
  if (error) throw error;
  return data || [];
}

export async function getPreferredDrivers(passengerUid) {
  const { data, error } = await supabase.rpc('get_preferred_drivers', {
    p_uid: passengerUid,
  });
  if (error) throw error;
  return data || [];
}

export async function isPassengerVip(passengerUid) {
  const { data, error } = await supabase.rpc('is_passenger_vip', {
    p_uid: passengerUid,
  });
  if (error) throw error;
  return data || false;
}

export async function createScheduledService(params) {
  const { data, error } = await supabase
    .from('scheduled_services')
    .insert({
      passenger_uid: params.passengerUid,
      pickup_time: params.pickupTime,
      recurrence: params.recurrence || 'none',
      weekdays: params.weekdays || null,
      end_date: params.endDate || null,
      pickup_address: params.pickupAddress,
      pickup_lat: params.pickupLat,
      pickup_lng: params.pickupLng,
      dropoff_address: params.dropoffAddress,
      dropoff_lat: params.dropoffLat,
      dropoff_lng: params.dropoffLng,
      reference_point: params.referencePoint || null,
      type: params.type || 'ride',
      preferred_driver_uid: params.preferredDriverUid || null,
      is_vip: params.isVip || false,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function cancelScheduledService(id) {
  const { error } = await supabase
    .from('scheduled_services')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function pauseScheduledService(id) {
  const { error } = await supabase
    .from('scheduled_services')
    .update({ status: 'paused', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function resumeScheduledService(id) {
  const { error } = await supabase
    .from('scheduled_services')
    .update({ status: 'active', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function togglePreferredDriver(passengerUid, driverUid, currentlyPreferred) {
  if (currentlyPreferred) {
    const { error } = await supabase
      .from('preferred_drivers')
      .delete()
      .eq('passenger_uid', passengerUid)
      .eq('driver_uid', driverUid);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('preferred_drivers')
      .insert({ passenger_uid: passengerUid, driver_uid: driverUid });
    if (error) throw error;
  }
}

// -- Tracking p�blico (SPEC-005: consumido por PublicTracking.jsx sin sesi�n) --

export async function getPublicTrackingByToken(token) {
  return supabase.rpc('get_tracking_by_token', { token });
}

// ============================================================
// SPEC-011 Fase 2: Anti Bait-and-Switch — Contraofertas
// ============================================================

/**
 * Conductor envía contraoferta de precio (máximo 1 por servicio).
 * @param {string} serviceId - service_id del servicio
 * @param {string} driverUid - UID del conductor
 * @param {number} newPrice - Nuevo precio propuesto
 * @returns {Promise<{success: boolean, error?: string, new_price?: number}>}
 */
export async function driverCounterOffer(serviceId, driverUid, newPrice) {
  const { data, error } = await supabase
    .rpc('driver_counter_offer', {
      p_service_id: serviceId,
      p_driver_uid: driverUid,
      p_new_price: newPrice,
    });
  if (error) throw error;
  return data;
}

/**
 * Pasajero responde a contraoferta del conductor.
 * @param {string} serviceId - service_id del servicio
 * @param {string} userUid - UID del pasajero
 * @param {boolean} accept - true=aceptar, false=rechazar
 * @returns {Promise<{success: boolean, action?: string, error?: string}>}
 */
export async function passengerRespondCounterOffer(serviceId, userUid, accept) {
  const { data, error } = await supabase
    .rpc('passenger_respond_counter_offer', {
      p_service_id: serviceId,
      p_user_uid: userUid,
      p_accept: accept,
    });
  if (error) throw error;
  return data;
}
